import { ApiResponse, http } from '@config/api';

export interface UserDto {
	id: string;
	name: string;
	surname: string;
	avatar: string;
	onlineStatus: number;
}

export enum OnlineStatus {
	Offline = 0,
	Online = 1,
}

export interface Attachment {
	fileName: string;
	s3_key: string;
	fileType: string;
	thumbnailKey: string;
	owner: string;
}

export enum MessageStatus {
	Active = 0,
	Deleted = 1,
	Edited = 2,
}

export interface Message {
	id: string;
	channelId: string;
	content: string | null;
	repliedMessageId: string | undefined;
	author: UserDto;
	mentions: UserDto[];
	attachments: Attachment[];
	createdAt: number;
	status: MessageStatus;
}

export interface ChannelLastMessage {
	id: string;
	content?: string | null;
	attachments: Attachment[];
	createdAt: number;
	status: MessageStatus;
}

export interface Channel {
	id: string;
	name: string;
	channel_type: number;
	request_id: string;
	offer_id: string;
	image: string;
	totalUsers: number;
	permission: string;
	notificationCount: number;
	isMuted: boolean;
	metadata: any;

	lastMessage?: ChannelLastMessage;
	users: UserDto[];
	UserLastReadTimestamps?: Record<string, number>;
}

export interface SendMessagePayload {
	messageId: string;
	repliedMessageId?: string;
	content: string;
	attachmentIds?: string[];
	mentionedUserIds?: string[];
}

export interface EditMessagePayload {
	repliedToMessageId?: string;
	content?: string;
	attachmentIds?: string[];
	mentionedUserIds?: string[];
}

export interface GetMessagesParams {
	lastMessageId?: string;
	limit?: number;
	order?: 'asc' | 'desc';
	search?: string;
}

export interface GetChannelsParams {
	limit?: number;
	order?: 'asc' | 'desc';
	lastChannelId?: string | null;
	type: number;
}

export interface SearchChannelsParams {
	q: string;
	type: number;
	lastChannelId?: string | null;
	limit?: number;
	order?: 'asc' | 'desc';
}

export interface UnreadMessagesCount {
	totalCount: number;
}

export class ChatService {
	private static instance: ChatService;

	private constructor() {}

	static getInstance(): ChatService {
		if (!ChatService.instance) {
			ChatService.instance = new ChatService();
		}
		return ChatService.instance;
	}

	// Get channels list
	async getChannels(params: GetChannelsParams): Promise<Channel[]> {
		const { limit = 10, order = 'desc', lastChannelId = null, type } = params;
		const queryParams = `limit=${limit}&order=${order}&type=${type}${lastChannelId ? `&lastChannelId=${lastChannelId}` : ''}`;
		const response = await http.get<ApiResponse<Channel[]>>('chat', `channels?${queryParams}`);
		return response.data.data;
	}

	// Search channels
	async searchChannels(params: SearchChannelsParams): Promise<Channel[]> {
		const { q, type, lastChannelId = null, limit = 10, order = 'desc' } = params;
		const queryParams = `q=${encodeURIComponent(q)}&type=${type}&limit=${limit}&order=${order}${lastChannelId ? `&lastChannelId=${lastChannelId}` : ''}`;
		const response = await http.get<ApiResponse<Channel[]>>('chat', `channels/search?${queryParams}`);
		return response.data.data;
	}

	// Get single channel
	async getChannel(channelId: string): Promise<Channel> {
		const response = await http.get<ApiResponse<Channel>>('chat', `channels/${channelId}`);
		return response.data.data;
	}

	// Seen Channel
	async seenChannel(channelId: string): Promise<void> {
		await http.post<ApiResponse<void>>('chat', `channels/${channelId}/seen`);
	}

	// Get messages in channel
	async getMessages(channelId: string, params: GetMessagesParams = {}): Promise<Message[]> {
		const { lastMessageId = '', limit = 10, order = 'desc' } = params;
		const response = await http.get<ApiResponse<Message[]>>(
			'chat',
			`channels/${channelId}/messages`,
			{
				lastMessageId,
				limit,
				order,
			}
		);
		return response.data.data;
	}

	// Search messages in channel - now uses the unified messages endpoint
	async searchMessages(channelId: string, search: string, params: Omit<GetMessagesParams, 'search'> = {}): Promise<Message[]> {
		const { lastMessageId = '', limit = 10, order = 'desc' } = params;
		// Include order parameter even for search to satisfy backend validation
		const queryParams: Record<string, string | number> = {
			search: search, // Don't encode here, let http.get handle it
			limit,
			order, // Include order parameter even though it may not be used for search results
		};
		
		if (lastMessageId) {
			queryParams.lastMessageId = lastMessageId;
		}
		
		const response = await http.get<ApiResponse<Message[]>>(
			'chat',
			`channels/${channelId}/messages`,
			queryParams
		);
		return response.data.data;
	}

	// Send message
	async sendMessage(channelId: string, payload: SendMessagePayload): Promise<Message> {
		const response = await http.post<ApiResponse<Message>>(
			'chat',
			`channels/${channelId}/messages`,
			payload
		);
		return response.data.data;
	}

	// Delete message
	async deleteMessage(channelId: string, messageId: string): Promise<void> {
		await http.delete<ApiResponse<void>>('chat', `channels/${channelId}/messages/${messageId}`);
	}

	// Edit message
	async editMessage(
		channelId: string,
		messageId: string,
		payload: EditMessagePayload
	): Promise<Message> {
		const response = await http.put<ApiResponse<Message>>(
			'chat',
			`channels/${channelId}/messages/${messageId}`,
			payload
		);
		return response.data.data;
	}

	async setChannelLanguage(channelId: string, language: string): Promise<void> {
		await http.patch<ApiResponse<void>>('chat', `channels/${channelId}/language/${language}`);
	}

	async sendHeartbeat(): Promise<void> {
		await http.get<ApiResponse<void>>('chat', 'heartbeat');
	}

	async getChannelUsers(channelId: string): Promise<UserDto[]> {
		const response = await http.get<ApiResponse<UserDto[]>>('chat', `channels/${channelId}/users`);
		return response.data.data;
	}

	async getDefaultChannels(): Promise<Channel[]> {
		const response = await http.get<ApiResponse<Channel[]>>('chat', 'chats');
		return response.data.data;
	}

	async getChannelsByRequestId(requestId: string): Promise<Channel[]> {
		const response = await http.get<ApiResponse<Channel[]>>('chat', `chats/request/${requestId}`);
		return response.data.data;
	}

	async getSpecialistChannelsByRequestIds(requestIds: string[]): Promise<Channel[]> {
		const queryParams = requestIds.map((id) => `requestIds=${id}`).join('&');
		const response = await http.get<ApiResponse<Channel[]>>(
			'chat',
			`chats/requests?${queryParams}`
		);
		return response.data.data;
	}

	// TODO: check if this is used, and remove if not
	async getChannelByRequestAndUserId(requestId: string, userId: string): Promise<Channel> {
		const response = await http.get<ApiResponse<Channel>>(
			'chat',
			`chats/request/${requestId}/user/${userId}`
		);
		return response.data.data;
	}

	async getChannelIdByRequestAndUserId(requestId: string, userId: string): Promise<string> {
		const response = await http.get<ApiResponse<Channel>>(
			'chat',
			`chats/request/${requestId}/user/${userId}`
		);
		return response.data.data?.id;
	}

	async getChannelByRequestAndOfferId(requestId: string, offerId: string): Promise<Channel> {
		const response = await http.get<ApiResponse<Channel>>(
			'chat',
			`chats/request/${requestId}/offer/${offerId}`
		);
		return response.data.data;
	}

	async GetChannelByMonitoringId(monitoringId: string): Promise<Channel> {
		const response = await http.get<ApiResponse<Channel>>(
			'chat',
			`chats/monitoring/${monitoringId}`
		);
		return response.data.data;
	}

	async getUnreadMessagesCount(): Promise<number> {
		const response = await http.get<ApiResponse<UnreadMessagesCount>>('chat', 'channels/notification-count');
		return response.data.data.totalCount;
	}
}

export const chatService = ChatService.getInstance();
